import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { comingSoon2027 } from '../../../data/events';
import type { ComingEvent } from '../../../data/events';

function useInView<T extends HTMLElement>(threshold = 0.3) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(true);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (element.getBoundingClientRect().top <= window.innerHeight) return;

    setInView(false);

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold },
    );

    observer.observe(element);
    const fallback = window.setTimeout(() => setInView(true), 2500);

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
    };
  }, [threshold]);

  return { ref, inView };
}

function useScrollProgress<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let animationFrame = 0;

    const compute = () => {
      const element = ref.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const activationLine = window.innerHeight * 0.66;
      const nextProgress = (activationLine - rect.top) / rect.height;
      setProgress(Math.max(0, Math.min(1, nextProgress)));
    };

    const onScroll = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return { ref, progress };
}

function ComingCard({ event }: { event: ComingEvent }) {
  return (
    <article
      className="w-full max-w-[27.5rem] overflow-hidden rounded-md border border-line bg-card"
      style={{ boxShadow: 'var(--app-shadow-soft)' }}
    >
      {event.image ? (
        <div className="aspect-[16/6] overflow-hidden border-b border-line-subtle bg-ink">
          <img
            src={event.image}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            width="1530"
            height="600"
          />
        </div>
      ) : (
        <div
          className="flex aspect-[16/6] items-center justify-center border-b border-line-subtle px-7 text-center"
          style={{ backgroundColor: event.tile ?? 'var(--bg-ink)' }}
        >
          <span className="max-w-[18ch] font-display text-[1.4rem] font-bold leading-tight text-white">
            {event.name.replace(' 2027', '')}
          </span>
        </div>
      )}

      <div className="p-6 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl font-bold leading-tight text-fg-primary">
            {event.name.replace(' 2027', '')}
          </h3>
          <span className="mt-1 shrink-0 rounded-sm bg-subtle px-2 py-1 font-ui text-xs font-semibold uppercase tracking-[0.08em] text-fg-muted">
            2027
          </span>
        </div>

        {event.inviteOnly && (
          <span className="mt-3 inline-block rounded-sm border border-action/35 px-2.5 py-1 font-ui text-xs font-semibold uppercase tracking-[0.1em] text-action">
            Exclusive invite-only
          </span>
        )}

        {event.date && (
          <p className="mt-3 font-ui text-[0.95rem] font-semibold text-fg-secondary">{event.date}</p>
        )}
        {event.venue && <p className="mt-1 font-body text-sm text-fg-muted">{event.venue}</p>}

        {event.href ? (
          <a
            href={event.href}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-5 inline-flex items-center gap-1.5 font-ui text-sm font-semibold text-action transition-colors hover:text-action-hover"
          >
            {event.ctaLabel}
            <ArrowRight
              size={15}
              strokeWidth={2}
              className="transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
        ) : (
          <p className="mt-5 flex items-center gap-2 font-ui text-xs font-semibold uppercase tracking-[0.1em] text-fg-muted">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-action" aria-hidden="true" />
            {event.ctaLabel}
          </p>
        )}
      </div>
    </article>
  );
}

function SpineDot({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={
        'relative z-10 h-4 w-4 shrink-0 rounded-full border-[3px] border-card transition-all duration-500 ease-out ' +
        (active ? 'scale-100 bg-action' : 'scale-50 bg-line')
      }
      style={{ boxShadow: '0 0 0 2px var(--border-default)' }}
    />
  );
}

function TimelineRow({ event }: { event: ComingEvent }) {
  const { ref, inView } = useInView<HTMLDivElement>(0.35);
  const [settled, setSettled] = useState(false);
  const left = event.side === 'left';
  const hiddenTransform = left ? '-translate-x-10 opacity-0' : 'translate-x-10 opacity-0';
  const cardClass = settled
    ? 'opacity-100'
    : `transition-[transform,opacity] duration-700 ease-out ${inView ? 'translate-x-0 opacity-100' : hiddenTransform}`;
  const connectorClass = `h-[2px] w-10 bg-action transition-transform duration-500 ease-out ${inView ? 'scale-x-100' : 'scale-x-0'}`;

  return (
    <div ref={ref} className="relative flex items-center">
      <div className="flex flex-1 justify-end">
        {left && (
          <div className="flex min-w-0 items-center" onTransitionEnd={() => inView && setSettled(true)}>
            <div className={cardClass}>
              <ComingCard event={event} />
            </div>
            <div className={`${connectorClass} origin-right`} aria-hidden="true" />
          </div>
        )}
      </div>
      <SpineDot active={inView} />
      <div className="flex flex-1 justify-start">
        {!left && (
          <div className="flex min-w-0 items-center" onTransitionEnd={() => inView && setSettled(true)}>
            <div className={`${connectorClass} origin-left`} aria-hidden="true" />
            <div className={cardClass}>
              <ComingCard event={event} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MobileRow({ event }: { event: ComingEvent }) {
  const { ref, inView } = useInView<HTMLDivElement>(0.25);

  return (
    <div ref={ref} className="relative">
      <span
        aria-hidden="true"
        className={
          'absolute -left-6 top-6 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-[3px] border-card transition-all duration-500 ease-out ' +
          (inView ? 'scale-100 bg-action' : 'scale-50 bg-line')
        }
        style={{ boxShadow: '0 0 0 2px var(--border-default)' }}
      />
      <div className={`transition-opacity duration-700 ease-out ${inView ? 'opacity-100' : 'opacity-0'}`}>
        <ComingCard event={event} />
      </div>
    </div>
  );
}

export function ComingSoon() {
  const { ref: spineRef, progress } = useScrollProgress<HTMLDivElement>();

  return (
    <section
      id="horizon"
      className="relative overflow-x-clip border-y border-line-subtle bg-subtle pb-section pt-section md:pb-0"
    >
      <div className="mx-auto max-w-content px-6 md:px-16">
        <p className="font-ui text-[0.8rem] font-semibold uppercase tracking-[0.12em] text-action">2027 season</p>
        <h2 className="mt-2.5 font-display text-4xl font-bold tracking-tight text-fg-primary md:text-[2.5rem]">
          On the Horizon
        </h2>
        <p className="mt-2 max-w-xl font-body text-lg text-fg-muted">
          Save the dates — full details for next year's events land soon.
        </p>

        <div ref={spineRef} className="relative mx-auto mt-12 hidden max-w-[67.5rem] pb-section md:block">
          <div className="absolute bottom-0 left-1/2 top-0 w-[2px] -translate-x-1/2 bg-line" aria-hidden="true" />
          <div
            className="absolute left-1/2 top-0 w-[2px] -translate-x-1/2 bg-action transition-[height] duration-150 ease-out"
            style={{ height: `${progress * 100}%` }}
            aria-hidden="true"
          />

          <div className="relative z-10 mb-14 flex justify-center" aria-hidden="true">
            <div className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-ink">
              <span className="font-display text-[1.65rem] font-bold leading-none text-white">2027</span>
            </div>
          </div>

          <div className="flex flex-col gap-14">
            {comingSoon2027.map((event) => (
              <TimelineRow key={event.name} event={event} />
            ))}
          </div>
        </div>

        <div className="relative mt-10 pl-9 md:hidden">
          <div className="absolute bottom-2 left-3 top-2 w-[2px] bg-line" aria-hidden="true" />
          <div className="flex flex-col gap-7">
            <div className="relative h-10" aria-hidden="true">
              <span className="absolute -left-6 top-1 flex h-[2.2rem] w-[2.2rem] -translate-x-1/2 items-center justify-center rounded-full bg-ink font-display text-xs font-bold text-white">
                27
              </span>
            </div>
            {comingSoon2027.map((event) => (
              <MobileRow key={event.name} event={event} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

